export class LearningOutcomePerformanceItem {
    constructor(data = {}) {
        this.id = data.id || null;
        this.topic = data.topic || '';
        this.learningOutcome = data.learningOutcome || '';
        this.learningActivities = data.learningActivities || '';
        this.assessment = data.assessment || '';
        this.remarks = data.remarks || '';
        this.teachingAid = data.teachingAid || '';
        this.competency = data.competency || '';
        this.securedMarks = Number(data.securedMarks) || 0;
        this.maximumMarks = Number(data.maximumMarks) || 0;
        this.percentage = data.percentage !== undefined && data.percentage !== null 
            ? Number(data.percentage) 
            : (this.maximumMarks > 0 ? parseFloat(((this.securedMarks / this.maximumMarks) * 100).toFixed(2)) : 0);
        this.totalEvaluations = Number(data.totalEvaluations) || 0;
        this.questionsCount = Number(data.questionsCount) || 0;
    }

    static fromJson(json) {
        if (!json || typeof json !== 'object') return new LearningOutcomePerformanceItem();
        return new LearningOutcomePerformanceItem(json);
    }

    get performanceLevel() {
        if (this.totalEvaluations === 0 && this.maximumMarks === 0) return 'UNTESTED';
        if (this.percentage >= 80) return 'EXCELLENT';
        if (this.percentage >= 60) return 'GOOD';
        if (this.percentage >= 40) return 'AVERAGE';
        return 'NEEDS_ATTENTION';
    }

    get statusBadgeColor() {
        switch (this.performanceLevel) {
            case 'EXCELLENT':
                return { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' };
            case 'GOOD':
                return { bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' };
            case 'AVERAGE':
                return { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' };
            case 'NEEDS_ATTENTION':
                return { bg: 'bg-rose-50 dark:bg-rose-950/40', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-200 dark:border-rose-800' };
            default:
                return { bg: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
        }
    }
}

export class LessonPlanItem {
    constructor(data = {}) {
        this.lessonPlanId = data.lessonPlanId || data.id || null;
        this.chapterName = data.chapterName || 'Untitled Chapter';
        this.totalSecuredMarks = Number(data.totalSecuredMarks) || 0;
        this.totalMaximumMarks = Number(data.totalMaximumMarks) || 0;
        this.overallPercentage = data.overallPercentage !== undefined && data.overallPercentage !== null 
            ? Number(data.overallPercentage) 
            : (this.totalMaximumMarks > 0 ? parseFloat(((this.totalSecuredMarks / this.totalMaximumMarks) * 100).toFixed(2)) : 0);
        this.totalEvaluations = Number(data.totalEvaluations) || 0;
        this.totalQuestions = Number(data.totalQuestions) || 0;
        this.learningOutcomes = Array.isArray(data.learningOutcomes) 
            ? data.learningOutcomes.map(item => LearningOutcomePerformanceItem.fromJson(item)) 
            : [];
    }

    static fromJson(json) {
        if (!json || typeof json !== 'object') return new LessonPlanItem();
        return new LessonPlanItem(json);
    }

    get performanceLevel() {
        if (this.totalEvaluations === 0 && this.totalMaximumMarks === 0) return 'UNTESTED';
        if (this.overallPercentage >= 80) return 'EXCELLENT';
        if (this.overallPercentage >= 60) return 'GOOD';
        if (this.overallPercentage >= 40) return 'AVERAGE';
        return 'NEEDS_ATTENTION';
    }

    get statusBadgeColor() {
        switch (this.performanceLevel) {
            case 'EXCELLENT':
                return { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' };
            case 'GOOD':
                return { bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' };
            case 'AVERAGE':
                return { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' };
            case 'NEEDS_ATTENTION':
                return { bg: 'bg-rose-50 dark:bg-rose-950/40', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-200 dark:border-rose-800' };
            default:
                return { bg: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
        }
    }
}

export class LessonPlanReport {
    constructor(data = {}) {
        this.classId = data.classId || null;
        this.subjectId = data.subjectId || null;
        this.subjectName = data.subjectName || '';
        this.totalLessonPlans = Number(data.totalLessonPlans) || (Array.isArray(data.lessonPlans) ? data.lessonPlans.length : 0);
        this.lessonPlans = Array.isArray(data.lessonPlans) 
            ? data.lessonPlans.map(item => LessonPlanItem.fromJson(item)) 
            : [];
    }

    static fromJson(json) {
        if (!json || typeof json !== 'object') return new LessonPlanReport();
        return new LessonPlanReport(json);
    }

    /**
     * Aggregated metrics across all lesson plans
     */
    get aggregatedMetrics() {
        let totalSecured = 0;
        let totalMax = 0;
        let totalEvaluations = 0;
        let totalQuestions = 0;
        let totalOutcomes = 0;

        for (const lp of this.lessonPlans) {
            totalSecured += lp.totalSecuredMarks;
            totalMax += lp.totalMaximumMarks;
            totalEvaluations += lp.totalEvaluations;
            totalQuestions += lp.totalQuestions;
            totalOutcomes += lp.learningOutcomes.length;
        }

        const overallPercentage = totalMax > 0 
            ? parseFloat(((totalSecured / totalMax) * 100).toFixed(2)) 
            : 0;

        return {
            totalSecuredMarks: parseFloat(totalSecured.toFixed(2)),
            totalMaximumMarks: parseFloat(totalMax.toFixed(2)),
            overallPercentage,
            totalEvaluations,
            totalQuestions,
            totalOutcomes,
            totalChapters: this.lessonPlans.length
        };
    }
}

export default LessonPlanReport;
